import React, { useEffect, useState } from 'react';
import { withFormik } from 'formik';
import * as Yup from 'yup';
// components
import Modal from 'shared/modals/Modal';
import { FormikInput, FormikSelect, FormikToggle } from 'shared/forms';
import toastr from 'shared/helpers/toastr';
import errorToastr from 'shared/helpers/errorToastr';
import LoadingModal from 'shared/loading/LoadingModal';
// fetch
import { getGwiEquipment, postGwiEquipment, deleteGwiEquipment } from 'src/fetch/equipmentType';
import { getEquipmentTypes, getEquipmentCategoryTypes } from 'src/fetch/equipmentType';
import { getOrganizationsLookup } from 'src/fetch/organizations';
import { getSchedulingTerminals } from 'src/fetch/schedulingTerminals';
import { default as isContainer } from 'is-container';

const EquipmentModal = ({
  isOpen,
  close,
  initialValues,
  setFieldValue,
  setFieldTouched,
  values,
  handleChange,
  handleBlur,
  handleSubmit,
  touched,
  errors,
  isValid,
  isSubmitting,
  setSubmitting,
  resetForm,
}) => {
  // static lists
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [categories, setCategories] = useState([]);

  const closeModal = () => {
    resetForm();
    close(false);
  };

  const deleteEquipment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await deleteGwiEquipment(values.id);
      resetForm();
      close(true);

      toastr('success', 'GWI Equipment Deleted Successfully.');
    } catch (error) {
      errorToastr({
        error,
        messagePrefix: 'GWI Equipment Delete Failed',
      });
      setSubmitting(false);
    }
  };

  const load = async () => {
    setSubmitting(true);

    try {
      const [equipmentTypesResponse, customersResponse, terminalsResponse, categoriesResponse] = await Promise.all([
        getEquipmentTypes(),
        getOrganizationsLookup({ Roles: ['Customer'] }),
        getSchedulingTerminals(),
        getEquipmentCategoryTypes(),
      ]);

      setCategories(categoriesResponse);
      setEquipmentTypes(equipmentTypesResponse);
      setCustomers(customersResponse);
      setTerminals(terminalsResponse);

      if (values.id) {
        const equipmentResponse = await getGwiEquipment(values.id);
        resetForm(equipmentResponse);
      }
    } catch (error) {
      errorToastr({
        error,
        messagePrefix: 'GWI Equipment Load Failed',
      });
      console.error(error);
    }

    setSubmitting(false);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Modal isOpen handleClose={closeModal} containerStyle='medium'>
      <div className='modal-header'>
        <h5 className='modal-title' id='add-equipment-modal'>
          {initialValues && initialValues.id ? 'Edit' : 'Add'} GWI Equipment
        </h5>
        <button className='button large close' onClick={closeModal} />
      </div>
      <section className={`equipment-form ${isSubmitting ? 'hide-overflow' : ''}`}>
        <LoadingModal isOpen={isSubmitting} isNested={true} />
        <form onSubmit={handleSubmit}>
          <section className='modal-body formik-form'>
            <section className='formik-column'>
              <div className='formik-row' style={{ marginBottom: '2rem' }}>
                <FormikSelect
                  onChange={(selected) => setFieldValue('equipmentCategory', selected?.value)}
                  wrapperClass='wrapper-three-column'
                  label='Category'
                  options={categories.map((x) => ({ label: x.text, value: x.value }))}
                  name='equipmentCategory'
                  value={values.equipmentCategory}
                  onBlur={setFieldTouched}
                  touched={touched}
                  errors={errors}
                />
                <FormikSelect
                  onChange={(selected) => setFieldValue('equipmentTypeId', selected.value)}
                  wrapperClass='wrapper-three-column'
                  label='Equipment Type'
                  options={equipmentTypes
                    .filter((e) => e.category === values.equipmentCategory)
                    .map((x) => ({ label: x.name, value: x.id, deleted: x.deleted }))}
                  name='equipmentTypeId'
                  filterDeleted
                  value={values.equipmentTypeId}
                  onBlur={setFieldTouched}
                  touched={touched}
                  errors={errors}
                />
                <FormikInput
                  wrapperClass='wrapper-three-column'
                  type='text'
                  label='Equipment Number'
                  name='equipmentNumber'
                  value={values.equipmentNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </div>
              <div className='formik-row'>
                <FormikSelect
                  onChange={(selected) => setFieldValue('schedulingTerminalId', selected.value)}
                  wrapperClass='wrapper-two-column'
                  label='Scheduler Terminal'
                  options={terminals.map((x) => ({ label: x.name, value: x.id, deleted: x.deleted }))}
                  name='schedulingTerminalId'
                  filterDeleted
                  value={values.schedulingTerminalId}
                  onBlur={setFieldTouched}
                  touched={touched}
                  errors={errors}
                />
                <FormikToggle
                  wrapperClass='wrapper-two-column'
                  label='In Service'
                  name='inService'
                  value={values.inService}
                  onToggle={(value) => setFieldValue('inService', value)}
                  touched={touched}
                  onTouch={() => setFieldTouched('inService', true)}
                  errors={errors}
                  onText='Yes'
                  offText='No'
                />
              </div>
              <div className='formik-row' style={{ marginBottom: '18rem' }}>
                <FormikSelect
                  onChange={(selected) => setFieldValue('organizationId', selected.value)}
                  wrapperClass='wrapper-two-column'
                  label='Customer Dedicated'
                  options={[
                    { label: 'Select', value: null },
                    ...customers.map((x) => ({ label: x.name, value: x.id, deleted: x.deleted })),
                  ]}
                  name='organizationId'
                  filterDeleted
                  value={values.organizationId}
                  onBlur={setFieldTouched}
                  touched={touched}
                  errors={errors}
                />
              </div>
            </section>
          </section>
          <section className='modal-footer toolbar toolbar--xxs'>
            <div className='toolbar--column toolbar--right'>
              <button
                type='submit'
                className='button large secondary text-align--center toolbar--button--margin'
                disabled={isSubmitting}
              >
                Submit
              </button>
              {initialValues && initialValues.id && (
                <button
                  id='delete-equipment-type-button'
                  type='button'
                  className='button large error text-align--center toolbar--button--margin'
                  disabled={isSubmitting}
                  onClick={deleteEquipment}
                >
                  Delete
                </button>
              )}
              <button
                id='add-equipment-cancel'
                type='button'
                className='button large white text-align--center margin--none--xxs'
                onClick={() => closeModal(false)}
              >
                Cancel
              </button>
            </div>
          </section>
        </form>
      </section>
    </Modal>
  );
};

const EquipmentSchema = Yup.object().shape({
  equipmentTypeId: Yup.number().required('Equipment Type is Required').typeError('Equipment type is required'),
  equipmentNumber: Yup.string()
    .required('Equipment Number is Required')
    .max(20, 'Equipment number may not contain more than 20 characters')
    .test('valid equipment number', 'Invalid Equipment Number', (number) => isContainer(number)),
  inService: Yup.boolean().required(),
  organizationId: Yup.number().nullable(),
  schedulingTerminalId: Yup.number('Scheduling Terminal is Required')
    .required()
    .typeError('Scheduling Terminal is Required'),
});

export default withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues = {} }) => {
    return {
      equipmentTypeId: null,
      equipmentNumber: '',
      organizationId: null,
      inService: true,
      ...initialValues,
    };
  },
  validationSchema: EquipmentSchema,
  handleSubmit: async (values, { setSubmitting, resetForm, props: { close } }) => {
    setSubmitting(true);

    try {
      await postGwiEquipment({ ...values });
      close(true);
      resetForm();

      toastr('success', 'GWI Equipment Saved Successfully.');
    } catch (error) {
      errorToastr({
        error,
        messagePrefix: 'GWI Equipment Save Failed!',
      });
      setSubmitting(false);
    }
  },
})(EquipmentModal);
